import { EnumTipoDomandaModuloDTOTipoAssociazione } from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { modalConfirm } from '@framework/engine';
import { useTranslation } from '@framework/i18n';
import { message } from 'antd';
import React, { useCallback } from 'react';

// TODO sostituire con dati reali da swagger quando disponibili
export interface ITriggerableModuleChange {
  idModulo: string;
  stato: 'AGGIUNTO' | 'RIMOSSO';
  tipo: EnumTipoDomandaModuloDTOTipoAssociazione;
  titolo: string;
}

export interface ITriggerableDifferences {
  idTipoDomanda: string;
  titolo: string;
  moduli: ITriggerableModuleChange[];
}

interface Params {
  title: string;
  content: (difference: ITriggerableDifferences[]) => React.ReactNode;
}

/**
 * Hook per la gestione della conferma al salvataggio in caso di modifiche ai moduli dinamici.
 *
 * Se non ci sono modifiche, la chiamata di update va a buon fine.
 * Se ci sono modofiche, la chiamata torna 406 e viene mostrato un modal di conferma.
 * In caso di conferma si procede con la chiamata di update con il parametro di forzatura salvataggio nell'header a true.
 */
export function useTriggerableModulesConfirmUpdate(params: Params) {
  const { content, title } = params;
  const { t } = useTranslation();
  const confirmUpdate = useCallback(
    async (params: {
      differences: ITriggerableDifferences[];
      onOk: (...inputs: any[]) => Promise<void>;
    }) => {
      const { differences, onOk } = params;

      if (differences.length === 0) {
        try {
          await onOk();
        } catch (e) {
          console.log("Errore durante l'aggiornamento: ", { e }); // prettier-ignore
          message.error(getNetworkErrorMessage(e, t("triggerable-modules-confirm-update.generic-error", 'Si è verificato un errore generico, riprovare più tardi'))); // prettier-ignore
        }
        return;
      }

      const isConfirmed = await modalConfirm({
        title,
        content: content(differences),
        closable: true,
        maskClosable: true,
        width: 650,
      });

      if (isConfirmed) {
        try {
          await onOk();
        } catch (e) {
          console.log("Errore durante l'aggiornamento: ", { e }); // prettier-ignore
          message.error(getNetworkErrorMessage(e, t("triggerable-modules-confirm-update.generic-error", 'Si è verificato un errore generico, riprovare più tardi'))); // prettier-ignore
        }
      }
    },
    [content, title],
  );

  return confirmUpdate;
}
